import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  LOCALE_ID,
  OnInit,
  ViewChild,
  inject,
  signal,
} from "@angular/core";
import { MatPaginatorIntl, PageEvent } from "@angular/material/paginator";
import { Sort } from "@angular/material/sort";
import { NavigationEnd, Router } from "@angular/router";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { MatChipSelectionChange } from "@angular/material/chips";
import { filter } from "rxjs/operators";

import { DatabaseCacheService } from "../../../data/cache/database-cache.service";
import { SHARED_IMPORTS } from "src/app/shared/standalone-imports";
import {
  HomeUserListViewModel,
  HomeUserListState,
  UserListRow,
  UserDataAttribute,
  ColumnDefinition,
} from "./home-user-list.viewmodel";
import { UserListViewMode } from '../../../services/home-user-list-state.service';
import { RecentUsersService } from '@app/core/layout/recent-users.service';
import { SecondaryPanelService } from '@app/core/layout/secondary-panel.service';
import { COMMON_I18N } from '@app/i18n';
import { parseDateInput } from '@app/core/date/date-time.util';
import {
  getRecordTypePrimaryLabel,
  getRecordTypeSecondaryLabel,
} from "../user-record-list/record-type-display.util";

@Component({
  selector: "app-home-user-list",
  templateUrl: "./home-user-list.component.html",
  styleUrls: ["./home-user-list.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: SHARED_IMPORTS,
  providers: [HomeUserListViewModel],
})
export class HomeUserListComponent implements OnInit, AfterViewInit {
  @ViewChild("scrollContainer") scrollContainer?: ElementRef<HTMLElement>;
  @ViewChild("searchInput") searchInput?: ElementRef<HTMLInputElement>;

  filterText = "";
  inactiveOnly = false;
  searchExpanded = signal(false);

  private readonly destroyRef = inject(DestroyRef);
  public readonly common = inject(DatabaseCacheService);
  private readonly localeId = inject(LOCALE_ID);
  private readonly router = inject(Router);
  private readonly paginatorIntl = inject(MatPaginatorIntl);
  private readonly vm = inject(HomeUserListViewModel);
  private readonly recentUsersService = inject(RecentUsersService);
  private readonly secondaryPanel = inject(SecondaryPanelService);
  readonly vm$ = this.vm.vm$;
  readonly sexLanguage = (this.localeId || '').toLowerCase().startsWith('en') ? 'en' : 'jp';
  private readonly protocolLocale: "ja" | "en" | "zh-TW" = (() => {
    const lower = (this.localeId || '').toLowerCase();
    return lower.startsWith('en') ? 'en' : lower.startsWith('zh') ? 'zh-TW' : 'ja';
  })();

  private readonly inactivityDaysThreshold = 90;
  private pendingScrollTop: number | null = null;

  ngOnInit(): void {
    this.configurePaginatorLabels();
    this.vm$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((state) => this.applyState(state));
    this.vm.init();

    this.router.events
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((event) => event instanceof NavigationEnd),
      )
      .subscribe(() => this.restoreScrollPosition());
  }

  onPaginatorChange(event: PageEvent): void {
    this.vm.updatePagination(event.pageIndex, event.pageSize);
  }

  onSortChange(sort: Sort): void {
    this.vm.updateSort(sort.active, sort.direction || "");
  }

  onTableScroll(): void {
    const el = this.scrollContainer?.nativeElement;
    if (!el) {
      return;
    }
    this.vm.updateScrollPosition(el.scrollTop);
  }

  ngAfterViewInit(): void {
    this.restoreScrollPosition();
  }

  navigateToSubpage(row: UserListRow): void {
    const baseUrl = this.getBaseUrl(row);
    if (!baseUrl) {
      return;
    }

    // Add to recent users before navigation
    this.addToRecentUsers(row);

    const selectedSectionGroupFunctions =
      this.getSelectedSectionGroupFunctions();
    if (selectedSectionGroupFunctions.includes("RecordList")) {
      this.router.navigate([...baseUrl, "records"]);
      return;
    }
    if (this.hasUserSummaryFeature(selectedSectionGroupFunctions)) {
      this.router.navigate([...baseUrl, "summaries"]);
      return;
    }
    if (selectedSectionGroupFunctions.includes("Dialog")) {
      this.router.navigate([...baseUrl, "dialogs"]);
      return;
    }
    if (selectedSectionGroupFunctions.includes("Video")) {
      this.router.navigate([...baseUrl, "medias"]);
    }
  }

  navigateToSummary(row: UserListRow, event?: Event): void {
    event?.stopPropagation();
    const baseUrl = this.getBaseUrl(row);
    if (!baseUrl) {
      return;
    }
    const selectedSectionGroupFunctions =
      this.getSelectedSectionGroupFunctions();
    if (!this.hasUserSummaryFeature(selectedSectionGroupFunctions)) {
      return;
    }
    this.router.navigate([...baseUrl, "summaries"]);
  }

  hasSummaryAccess(): boolean {
    return this.hasUserSummaryFeature(this.getSelectedSectionGroupFunctions());
  }

  getUserDataItemValueCompare(
    userData: UserListRow | null,
    key: string,
  ): string {
    if (!userData) {
      console.info(
        "[INFO] home-user-list.component.ts > getUserDataItemValue()| selectedUserWithUserData === null.",
      );
      return "-";
    }

    const newestVal = this.getUserDataItemValue(userData, key, "newest");
    const secondNewestVal = this.getUserDataItemValue(
      userData,
      key,
      "secondNewest",
    );

    if (newestVal === "") {
      return "-";
    }

    return secondNewestVal !== ""
      ? `${newestVal}(${secondNewestVal})`
      : newestVal;
  }

  getUserDataItemNewestValue(
    userData: UserListRow | null,
    key: string,
  ): string {
    return this.getUserDataItemValue(userData, key, "newest");
  }

  getUserDataItemsNewestAndSecondAreEqual(
    userData: UserListRow | null,
    key: string,
  ): boolean {
    const newestItem = this.getUserDataItemValue(userData, key, "newest");
    const secondNewestItem = this.getUserDataItemValue(
      userData,
      key,
      "secondNewest",
    );

    if (newestItem === undefined || secondNewestItem === undefined) {
      return false;
    }
    return newestItem === secondNewestItem;
  }

  getUserDataItemValue(
    userData: UserListRow | null,
    key: string,
    generation: string,
  ): string {
    if (!userData?.userData) {
      return "";
    }

    const userDataNewest = userData.userData[generation];
    if (!userDataNewest || !userDataNewest.data) {
      return "";
    }

    const value = userDataNewest.data[key];
    return value ?? "";
  }

  getDate(dateStr?: string | null): string | null {
    if (!dateStr) {
      return null;
    }

    try {
      const date = parseDateInput(dateStr);
      if (!date) {
        return null;
      }
      return date.toLocaleString(undefined, {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
    } catch {
      return null;
    }
  }

  getProtocolPrimaryDisplay(protocol?: string | null): string {
    if (!protocol) {
      return "-";
    }
    return getRecordTypePrimaryLabel(protocol, protocol, this.protocolLocale);
  }

  getProtocolSecondaryDisplay(protocol?: string | null): string {
    if (!protocol || this.protocolLocale === "en") {
      return "";
    }
    const primary = this.getProtocolPrimaryDisplay(protocol);
    return getRecordTypeSecondaryLabel(protocol, primary);
  }

  getProtocolTooltip(protocol?: string | null): string {
    const primary = this.getProtocolPrimaryDisplay(protocol);
    const secondary = this.getProtocolSecondaryDisplay(protocol);
    return secondary ? `${primary} (${secondary})` : primary;
  }

  applyFilter(event?: Event): void {
    if (event && "target" in event) {
      const target = event.target as HTMLInputElement | null;
      this.filterText = target?.value ?? this.filterText;
      this.vm.updateFilterText(this.filterText);
    }
  }

  // M3 Filter Chip selection handler
  onInactiveFilterChange(event: MatChipSelectionChange): void {
    this.inactiveOnly = event.selected;
    this.vm.toggleInactiveOnly(event.selected);
  }

  // View mode toggle
  toggleViewMode(mode: UserListViewMode): void {
    this.vm.updateViewMode(mode);
  }

  /** Check if a column is visible in current state */
  isColumnVisible(state: HomeUserListState, columnDef: string): boolean {
    const cd = state.columnDefinitions.find(c => c.def === columnDef);
    return cd?.visible !== false;
  }

  /** Returns visible dynamic attributes for card view, respecting columnDefinitions visibility */
  getCardDynamicAttrs(state: HomeUserListState): UserDataAttribute[] {
    const attrs: UserDataAttribute[] = [];
    for (let i = 0; i < state.userDataAttributes.length; i++) {
      const group = state.userDataAttributes[i];
      for (let j = 0; j < group.dataItems.length; j++) {
        const attr = group.dataItems[j];
        if (attr.enabled && attr.showOnSummary) {
          const colDef = state.columnDefinitions.find(cd => cd.def === `column${i}${j}`);
          if (colDef?.visible !== false) {
            attrs.push(attr);
          }
        }
      }
    }
    return attrs;
  }

  // Toolbar actions
  refreshData(): void {
    this.vm.refreshData();
  }

  toggleColumnVisibility(columnDef: string): void {
    this.vm.toggleColumnVisibility(columnDef);
  }

  openCreateUser(): void {
    const sectionId = this.common.getSelectedSection()?.sectionId as string | undefined;
    if (sectionId) {
      this.secondaryPanel.toggleQuickAction('createUser', sectionId);
    }
  }

  clearSearch(): void {
    this.filterText = '';
    this.vm.updateFilterText('');
  }

  expandSearch(): void {
    this.searchExpanded.set(true);
    setTimeout(() => this.searchInput?.nativeElement.focus(), 0);
  }

  collapseSearch(): void {
    if (!this.filterText.trim()) {
      this.searchExpanded.set(false);
    }
  }

  onSearchBlur(): void {
    this.collapseSearch();
  }

  isInactive(row: UserListRow): boolean {
    const lastUpdated =
      row.userStatistics?.data?.lastUpdatedProtocolCreatedAt ?? null;
    if (!lastUpdated) {
      return true;
    }
    const parsed = parseDateInput(lastUpdated);
    if (!parsed) {
      return true;
    }
    const updatedAt = parsed.getTime();
    const diffDays = (Date.now() - updatedAt) / (1000 * 60 * 60 * 24);
    return diffDays >= this.inactivityDaysThreshold;
  }

  private configurePaginatorLabels(): void {
    this.paginatorIntl.itemsPerPageLabel = COMMON_I18N.paginator.itemsPerPage;
    this.paginatorIntl.nextPageLabel = COMMON_I18N.paginator.nextPage;
    this.paginatorIntl.previousPageLabel = COMMON_I18N.paginator.previousPage;
    this.paginatorIntl.firstPageLabel = COMMON_I18N.paginator.firstPage;
    this.paginatorIntl.lastPageLabel = COMMON_I18N.paginator.lastPage;
    this.paginatorIntl.getRangeLabel = (
      page: number,
      pageSize: number,
      length: number,
    ) => {
      if (length === 0 || pageSize === 0) {
        return `0 / ${length}`;
      }
      length = Math.max(length, 0);
      const startIndex = page * pageSize;
      const endIndex =
        startIndex < length
          ? Math.min(startIndex + pageSize, length)
          : startIndex + pageSize;

      return `${startIndex + 1} - ${endIndex} / ${length}`;
    };
    this.paginatorIntl.changes.next();
  }

  private applyState(state: HomeUserListState): void {
    this.filterText = state.filterText;
    this.inactiveOnly = state.inactiveOnly;
    this.pendingScrollTop = state.scrollTop;
    if (state.filterText.trim()) {
      this.searchExpanded.set(true);
    }
    this.restoreScrollPosition();
  }

  private restoreScrollPosition(): void {
    if (!this.scrollContainer) {
      return;
    }
    const restoredScrollTop = this.pendingScrollTop ?? 0;
    this.scrollContainer.nativeElement.scrollTop = restoredScrollTop;
  }

  private getBaseUrl(row: UserListRow): string[] | null {
    const selectedSection = this.common.getSelectedSection();
    if (!selectedSection) {
      return null;
    }
    return ["home", selectedSection.sectionId, "users", row.user.userId];
  }

  private getSelectedSectionGroupFunctions(): string[] {
    const selectedSectionGroupFunctions =
      this.common.getSelectedSectionGroupFunctions() ?? [];
    if (!Array.isArray(selectedSectionGroupFunctions)) {
      return [];
    }
    return selectedSectionGroupFunctions;
  }

  private hasUserSummaryFeature(features: string[]): boolean {
    return features.includes("UserData")
      || features.includes("userSummary")
      || features.includes("UserSummary");
  }

  private addToRecentUsers(row: UserListRow): void {
    const selectedSection = this.common.getSelectedSection();
    if (!selectedSection || !row.user.userId) {
      return;
    }

    const displayName =
      row.user.code ||
      [row.user.lastName, row.user.firstName].filter(Boolean).join(" ") ||
      row.user.userId;

    this.recentUsersService.addRecentUser({
      id: row.user.userId,
      name: displayName,
      sectionId: selectedSection.sectionId,
      sex: row.user.sex,
    });
  }
}
